// src/config.js
// Single source of truth for all environment configuration.
// NOTHING else in the codebase reads import.meta.env directly.
// Import: import { API, FIREBASE, FEATURES } from '@config'
//
// Vite replaces import.meta.env.VITE_* at build time.
// Missing required vars throw at startup — fail fast, not silently.

// ── Validation helper ─────────────────────────────────────────────
function required(key) {
  const val = import.meta.env[key];
  if (!val) throw new Error(`Missing required env var: ${key}`);
  return val;
}
function optional(key, fallback = '') {
  return import.meta.env[key] ?? fallback;
}
function optionalInt(key, fallback) {
  const v = import.meta.env[key];
  return v ? parseInt(v, 10) : fallback;
}
function optionalBool(key, fallback = false) {
  const v = import.meta.env[key];
  if (v === undefined || v === '') return fallback;
  return v === 'true';
}

// ── App ───────────────────────────────────────────────────────────
export const APP = {
  name:    optional('VITE_APP_NAME', 'Eye on Storm'),
  url:     optional('VITE_APP_URL',  'https://eyeonstorm.netlify.app'),
  version: optional('VITE_APP_VERSION', '2.0.0'),
  isDev:   import.meta.env.DEV,
  isProd:  import.meta.env.PROD,
};

// ── Firebase ──────────────────────────────────────────────────────
// Only resolved when VITE_ENABLE_FIREBASE=true.
// Throws at startup if Firebase is enabled but keys are missing.
export const FIREBASE = optionalBool('VITE_ENABLE_FIREBASE')
  ? {
      apiKey:            required('VITE_FIREBASE_API_KEY'),
      authDomain:        required('VITE_FIREBASE_AUTH_DOMAIN'),
      projectId:         required('VITE_FIREBASE_PROJECT_ID'),
      storageBucket:     required('VITE_FIREBASE_STORAGE_BUCKET'),
      messagingSenderId: required('VITE_FIREBASE_MESSAGING_SENDER_ID'),
      appId:             required('VITE_FIREBASE_APP_ID'),
      measurementId:     optional('VITE_FIREBASE_MEASUREMENT_ID'),
    }
  : null;

// ── NWS / NOAA ────────────────────────────────────────────────────
const NWS_BASE = optional('VITE_NWS_BASE_URL', 'https://api.weather.gov');
const NWS_AREA = optional('VITE_NWS_ALERTS_AREA', 'LA');
const NWS_LIMIT = optional('VITE_NWS_ALERTS_LIMIT', '20');

export const NWS = {
  baseUrl:    NWS_BASE,
  officeUrl:  optional('VITE_NWS_OFFICE_URL', 'https://www.weather.gov/lix/'),
  alertsUrl:  `${NWS_BASE}/alerts/active?area=${NWS_AREA}&status=actual&limit=${NWS_LIMIT}`,
};

// ── NOAA Radar ────────────────────────────────────────────────────
export const RADAR = {
  station:    optional('VITE_RADAR_STATION', 'KLIX'),
  baseUrl:    optional('VITE_RADAR_BASE_URL',   'https://ridge.weather.gov/ridge/standard'),
  iframeUrl:  optional('VITE_RADAR_IFRAME_URL', 'https://radar.weather.gov/station/KLIX/standard'),
};

// ── USGS Stream Gauges ────────────────────────────────────────────
const USGS_BASE   = optional('VITE_USGS_BASE_URL', 'https://waterservices.usgs.gov/nwis/iv');
const USGS_GAUGES = optional('VITE_USGS_GAUGE_IDS', '07374525,07374000');
const USGS_PARAM  = optional('VITE_USGS_PARAM_CD', '00065');
const USGS_PERIOD = optional('VITE_USGS_PERIOD', 'PT2H');

export const USGS = {
  baseUrl:  USGS_BASE,
  gaugeIds: USGS_GAUGES.split(','),
  gaugesUrl: `${USGS_BASE}/?format=json&sites=${USGS_GAUGES}&parameterCd=${USGS_PARAM}&period=${USGS_PERIOD}`,
};

// ── 511LA / DOTD Traffic ──────────────────────────────────────────
const DOTD_BASE     = optional('VITE_511LA_BASE_URL', 'https://511la.org/api/v2/get/event');
const DOTD_KEY      = optional('VITE_511LA_API_KEY', '48f89720b153483eac0d916810e16d08');
const DOTD_PARISHES = optional('VITE_511LA_PARISHES', 'orleans,jefferson,stbernard,plaquemines');

export const DOTD = {
  publicUrl:  optional('VITE_511LA_PUBLIC_URL', 'https://www.511la.org'),
  apiUrl:     `${DOTD_BASE}?key=${DOTD_KEY}&format=json&area=${DOTD_PARISHES}`,
};

// ── CORS Proxies ──────────────────────────────────────────────────
export const CORS_PROXIES = [
  optional('VITE_CORS_PROXY_1', 'https://corsproxy.io/?url='),
  optional('VITE_CORS_PROXY_2', 'https://api.allorigins.win/get?url='),
  optional('VITE_CORS_PROXY_3', 'https://api.codetabs.com/v1/proxy?quest='),
].filter(Boolean);

// ── Timeouts (ms) ─────────────────────────────────────────────────
export const TIMEOUT = {
  alerts:  optionalInt('VITE_FETCH_TIMEOUT_ALERTS',  8000),
  traffic: optionalInt('VITE_FETCH_TIMEOUT_TRAFFIC', 7000),
  gauges:  optionalInt('VITE_FETCH_TIMEOUT_GAUGES',  8000),
};

// ── Refresh intervals (ms) ────────────────────────────────────────
// ── Refresh intervals ────────────────────────────────────────────
// REAL-TIME: alerts, incidents, SOS — users need these immediately
// NOT REAL-TIME: shelters, contacts, radio — static/semi-static
//
// Low Power Mode multiplies all intervals by 4× (see useLowPowerMode.js)
export const REFRESH = {
  // Real-time tier
  alerts:      optionalInt('VITE_REFRESH_ALERTS_MS',       3 * 60 * 1000),   //  3 min
  traffic:     optionalInt('VITE_REFRESH_TRAFFIC_MS',      5 * 60 * 1000),   //  5 min
  gauges:      optionalInt('VITE_REFRESH_GAUGES_MS',       5 * 60 * 1000),   //  5 min
  incidents:   optionalInt('VITE_REFRESH_INCIDENTS_MS',    5 * 60 * 1000),   //  5 min
  outages:     optionalInt('VITE_REFRESH_OUTAGES_MS',      7 * 60 * 1000),   //  7 min
  gas:         optionalInt('VITE_REFRESH_GAS_MS',          6 * 60 * 1000),   //  6 min
  weather:     optionalInt('VITE_REFRESH_WEATHER_MS',     10 * 60 * 1000),   // 10 min
  // Static / semi-static tier — served from snapshots/CDN
  shelters:    optionalInt('VITE_REFRESH_SHELTERS_MS',    30 * 60 * 1000),   // 30 min
  contacts:    optionalInt('VITE_REFRESH_CONTACTS_MS',    60 * 60 * 1000),   //  1 hr
  radio:       optionalInt('VITE_REFRESH_RADIO_MS',   24 * 60 * 60 * 1000), // 24 hr
  evacuation:  optionalInt('VITE_REFRESH_EVAC_MS',    24 * 60 * 60 * 1000), // 24 hr
  // Map-specific
  mapRefresh:  optionalInt('VITE_REFRESH_MAP_MS',         30 * 1000),        // 30 sec min
};

// ── Rate limits (client-side enforcement) ────────────────────────
// Server enforces the same limits independently.
// Client limits prevent accidental hammering and give UX feedback.
export const RATE_LIMITS = {
  report:          { max: 3, windowMs:  5 * 60 * 1000 },  //  3 reports / 5 min
  checkIn:         { max: 5, windowMs:  5 * 60 * 1000 },  //  5 check-ins / 5 min
  mapRefresh:      { max: 1, windowMs:     30 * 1000  },  //  1 refresh / 30 sec
  weatherRefresh:  { max: 1, windowMs:  5 * 60 * 1000 },  //  1 refresh / 5 min
  shelterRefresh:  { max: 1, windowMs: 10 * 60 * 1000 },  //  1 refresh / 10 min
  sosReport:       { max: 5, windowMs:  5 * 60 * 1000 },  //  5 SOS / 5 min (generous)
};

// ── SMS configuration ─────────────────────────────────────────────
export const SMS = {
  inboundNumber:  import.meta.env.VITE_SMS_INBOUND_NUMBER ?? '+15045550100',
  enabled:        import.meta.env.VITE_SMS_ENABLED === 'true',
};

// ── Feature flags ─────────────────────────────────────────────────
export const FEATURES = {
  firebase:           optionalBool('VITE_ENABLE_FIREBASE',            false),
  pushNotifications:  optionalBool('VITE_ENABLE_PUSH_NOTIFICATIONS',  false),
  analytics:          optionalBool('VITE_ENABLE_ANALYTICS',           false),
};

// ── Sentry ────────────────────────────────────────────────────────
export const SENTRY = {
  dsn:               optional('VITE_SENTRY_DSN'),
  environment:       optional('VITE_SENTRY_ENVIRONMENT', 'development'),
  enabled:           optionalBool('VITE_SENTRY_ENABLED', false),
  tracesSampleRate:  parseFloat(optional('VITE_SENTRY_TRACES_SAMPLE_RATE', '0.2')),
  replaysSampleRate: parseFloat(optional('VITE_SENTRY_REPLAYS_SAMPLE_RATE', '0.05')),
  replaysOnErrorRate:parseFloat(optional('VITE_SENTRY_REPLAYS_ON_ERROR_RATE', '1.0')),
  release:           `eyeonstorm@${optional('VITE_APP_VERSION', '2.0.0')}`,
};

// ── NOAA Tides & Currents ─────────────────────────────────────────
// Tidal and storm surge data for Gulf Coast stations
export const NOAA_TIDES = {
  baseUrl:    'https://tidesandcurrents.noaa.gov/api/prod/datagetter',
  // New Orleans area NOAA station IDs
  stations: {
    'grand_isle':      '8761724',   // Grand Isle, LA — Gulf surge indicator
    'new_canal':       '8761927',   // New Canal Station, New Orleans
    'calcasieu_pass':  '8768094',   // Calcasieu Pass — western surge
    'pilot_station':   '8760922',   // Pilot Station East, lower Mississippi
  },
  product:   'water_level',
  datum:     'NAVD',
  units:     'english',
  timeZone:  'lst_ldt',
};

// ── USGS extended gauge network ───────────────────────────────────
const USGS_BASE_URL   = optional('VITE_USGS_BASE_URL', 'https://waterservices.usgs.gov/nwis/iv');
// Full New Orleans metro gauge network (16 stations)
const USGS_ALL_GAUGES = optional('VITE_USGS_GAUGE_IDS',
  '07374525,07374000,07375000,07374800,07374828,' +
  '07374581,07374795,07380251,07381335,07381349,' +
  '07381600,07383000,07371500,07355010,07290650,07301410'
);
export const USGS_EXTENDED = {
  baseUrl:   USGS_BASE_URL,
  gaugeIds:  USGS_ALL_GAUGES.split(','),
  gaugesUrl: `${USGS_BASE_URL}/?format=json&sites=${USGS_ALL_GAUGES}&parameterCd=00065&period=PT2H`,
  // Flood inundation service
  floodUrl:  'https://waterservices.usgs.gov/nwis/flood',
};

// ── Entergy outage map (community-documented endpoint) ────────────
// This endpoint powers entergy.com's outage map — no auth required.
// Returns JSON with parish-level outage counts.
// NOTE: unofficial — monitor for breaking changes after storms.
export const ENTERGY = {
  outageUrl: 'https://outage.entergy.com/resources/php/googlemaps/outage_maps.php',
  // Parish FIPS codes for Louisiana parishes we cover
  parishes: {
    'Orleans':    '22071',
    'Jefferson':  '22051',
    'St. Bernard':'22087',
    'Plaquemines':'22075',
    'St. Tammany':'22103',
    'St. Charles':'22089',
    'St. John':   '22095',
  },
  // Refresh: 5 min during storms, 15 min otherwise
  refreshMs: optionalInt('VITE_REFRESH_ENTERGY_MS', 5 * 60 * 1000),
};

// ── GOHSEP RSS feed ───────────────────────────────────────────────
export const GOHSEP = {
  // Governor's Office of Homeland Security & Emergency Preparedness
  rssUrl:     'https://gohsep.la.gov/about/news?format=rss',
  // Also check the state emergency page
  newsUrl:    'https://www.emergency.louisiana.gov',
  refreshMs:  optionalInt('VITE_REFRESH_GOHSEP_MS', 10 * 60 * 1000),
};

// ── 511LA extended ────────────────────────────────────────────────
const DOTD_KEY_EXT  = optional('VITE_511LA_API_KEY', '48f89720b153483eac0d916810e16d08');
const DOTD_AREA_EXT = optional('VITE_511LA_PARISHES', 'orleans,jefferson,stbernard,plaquemines');
export const DOTD_EXTENDED = {
  publicUrl:    optional('VITE_511LA_PUBLIC_URL', 'https://www.511la.org'),
  eventsUrl:    `https://511la.org/api/v2/get/event?key=${DOTD_KEY_EXT}&format=json&area=${DOTD_AREA_EXT}`,
  messagesUrl:  `https://511la.org/api/v2/get/message?key=${DOTD_KEY_EXT}&format=json&area=${DOTD_AREA_EXT}`,
  camerasUrl:   `https://511la.org/api/v2/get/camera?key=${DOTD_KEY_EXT}&format=json&area=${DOTD_AREA_EXT}`,
};
